/**
 * Every string a guest can see.
 *
 * A typed catalogue rather than a library. This app has a few dozen strings on a screen a diner opens over
 * cellular, and i18next plus its React bindings would add roughly 40 KB gzipped — a real cost against a
 * ~97 KB bundle, for features (namespaces, lazy catalogues, ICU) nothing here needs.
 *
 * What it buys instead: `Messages` is derived from the English catalogue, so **a missing or misspelled
 * Spanish key is a compile error**. No runtime key lookups returning the key back to a guest, which is the
 * usual failure mode of string-keyed i18n.
 *
 * When to abandon this: plural rules beyond simple count, gendered agreement, or catalogues large enough to
 * want lazy loading. At that point reach for a library rather than growing this.
 *
 * The legacy app's shape - 78 KB of exported arrays of `{ key: 'en-us', value }` per string - is what this is
 * deliberately not.
 */

const en = {
    // Check screen
    'check.table': 'Table {table}',
    'check.number': 'Check {number}',
    'check.subtotal': 'Subtotal',
    'check.tax': 'Tax',

    /**
     * Shown instead of `check.tax` at a location that charges a digital service fee, for the tax and the fee
     * added together. A location with no fee keeps `check.tax` — two labels for one row, deliberately: a guest
     * only ever sees one restaurant, and a row that says "Tax" for a figure containing a fee does not say what
     * they are paying.
     */
    'check.taxesAndFees': 'Taxes and Fees',
    /** Screen-reader name for the information button beside it, which is otherwise an unlabelled glyph. */
    'check.feeInfoLabel': 'About taxes and fees',
    /** The disclosure itself. {amount} is the fee alone, never the summed figure in the row. */
    'check.feeExplanation':
        'Taxes & Fees includes all applicable local/state taxes and a Digital Service Fee of {amount}.',
    'check.total': 'Total',
    /** One named discount line. Falls back to this when the POS names the discount nothing. */
    'check.discount': 'Discount',
    /** One named service charge line, same fallback. */
    'check.surcharge': 'Service charge',
    /** Both shown only on a check somebody has already part-paid. */
    'check.alreadyPaid': 'Already paid',
    'check.amountDue': 'Amount due',
    'check.pay': 'Pay this check',
    'check.closedAction': 'Check closed',
    'check.closedNotice': 'This check has already been closed. Ask your server if you think that’s wrong.',
    'check.empty': 'Nothing has been added to this check yet.',
    /** Screen-reader label for the quantity badge, which is otherwise a bare number. */
    'check.quantityLabel': 'Quantity {count}',
    'check.itemsLabel': 'Items on your check',

    // Tip screen
    'tip.title': 'Add a tip',
    'tip.optionsLabel': 'Tip amount',
    'tip.custom': 'Custom',
    'tip.customLabel': 'Custom tip amount',
    'tip.customPlaceholder': 'Enter an amount',
    /** Shown when what the guest typed is not an amount at all. */
    'tip.customInvalid': 'Enter an amount, for example {example}.',
    /**
     * A service charge the POS applied, already inside the amount due. Deliberately not called a gratuity:
     * a surcharge may be a large-party gratuity or may be something else entirely, and the check does not say.
     */
    'tip.serviceCharge': 'A service charge of {amount} is already included in this check.',
    'tip.checkLine': 'Check',
    'tip.tipLine': 'Tip',
    'tip.pay': 'Pay {amount}',
    'tip.back': 'Back to the check',

    // Payment method screen
    'payment.title': 'How would you like to pay?',
    'payment.optionsLabel': 'Payment method',
    'payment.card': 'Card',
    'payment.applePay': 'Apple Pay',
    'payment.googlePay': 'Google Pay',
    'payment.continue': 'Continue',
    'payment.back': 'Back to the tip',

    // The same link, when the tip step does not exist for this location. See PaymentMethodScreen.
    'payment.backToCheck': 'Back to the check',

    /** Names the gateway's iframe. An unnamed frame is announced as "frame", which says nothing. */
    'payment.cardFrameTitle': 'Card details',
    'payment.resolving': 'Finishing your payment',
    /** The submit control for the gateway's iframe, which renders card fields and no button of its own. */
    'payment.payCard': 'Pay',
    'payment.paying': 'Paying…',

    // Receipt / confirmation screen
    'receipt.paidTitle': 'Payment complete',
    /** Announced to a screen reader the moment the screen appears, before anything is read out. */
    'receipt.paidAnnouncement': 'Payment complete. {amount} paid.',
    'receipt.checkNumber': 'Check #',
    /** Who served the table. Only shown when the POS tracks it, which most vendors do not. */
    'receipt.server': 'Server',
    'receipt.date': 'Date',
    'receipt.paymentLabel': 'Payment details',
    'receipt.approvalCode': 'Approval code',

    // Receipt delivery. The destination is named in the confirmation for the same reason the loyalty code
    // screen names it: that is how a guest spots their own typo.
    'receipt.deliveryTitle': 'Want a copy?',
    'receipt.emailIt': 'Email it',
    'receipt.textIt': 'Text it',
    'receipt.emailLabel': 'Email address',
    'receipt.smsLabel': 'Mobile number',
    'receipt.emailPlaceholder': 'you@example.com',
    'receipt.smsPlaceholder': '(555) 123-4567',
    'receipt.send': 'Send',
    'receipt.sending': 'Sending…',
    'receipt.emailSent': 'Sent to {destination}.',
    'receipt.smsSent': 'Texted to {destination}.',
    'receipt.deliveryFailed': 'We couldn’t send it. Check the details and try again.',
    // A 429. "Too many" tells a guest to wait; a generic failure tells them to retry, which is wrong here.
    'receipt.deliveryTooMany': 'That’s a few too many tries. Wait a minute, then send it again.',
    'receipt.receiptLabel': 'Your receipt',
    'receipt.thankYou': 'Thank you for dining with us',
    'receipt.feedback': 'Tell us how we did',
    /** Only on the link that leaves the app — see the note in `ReceiptScreen`. */
    'receipt.feedbackExternal': 'Opens in a new tab',
    /** Card number, spoken. The bullets on screen are decoration a screen reader should not read out. */
    'receipt.cardEnding': 'Card ending {lastFour}',

    /*
     * Only on the tablet, where the staff need their screen back and the app has to be told the guest is
     * finished — see `TabletDone`. Worded for the guest handing the tablet back rather than for the staff:
     * "Done" is what they have just decided, and it is the same word whether they answered the questions or
     * scrolled past them.
     */
    'receipt.done': 'Done',
    /** After the tap, while the tablet takes its screen back. Never seen for long, and never on a phone. */
    'receipt.doneFinishing': 'Finishing…',

    /*
     * Loyalty, shown between the check and the tip when the restaurant runs a programme.
     *
     * The programme's NAME and the checkout prompt are not here — they are authored per brand and arrive over
     * the network in whatever language the restaurant wrote them, like the feedback questions above. So are
     * the offer and reward names.
     */
    'loyalty.signIn': 'Sign in',
    'loyalty.join': 'Join the programme',
    'loyalty.skip': 'Not now',
    'loyalty.continue': 'Continue',
    'loyalty.working': 'Just a moment…',
    /** Labels the one identifier field, worded for what this brand actually accepts. */
    'loyalty.identifier.phone': 'Mobile number',
    'loyalty.identifier.email': 'Email address',
    'loyalty.identifier.either': 'Mobile number or email',
    'loyalty.code': 'Verification code',
    /** The masked destination is the point of this line — it is how a guest spots a mistyped number. */
    'loyalty.codeSent': 'We sent a code to {destination}.',
    'loyalty.verify': 'Verify',
    'loyalty.codeWrong': 'That code didn’t work. Check it and try again.',
    'loyalty.error': 'Something went wrong. Please try again.',
    'loyalty.notFound': 'We couldn’t find that account. You can still pay as normal.',
    'loyalty.greeting': 'Hi {name}',
    'loyalty.points': '{points} points',
    'loyalty.offers': 'Your offers',
    'loyalty.rewards': 'Redeem points',
    /** Beneath a reward, next to what it takes off the check. */
    'loyalty.pointCost': '{points} points',
    'loyalty.nothingAvailable': 'Nothing to redeem on this check right now.',
    /** Shown only once the limit is hit, so it needs no count — the guest can see what they picked. */
    'loyalty.limitReached': 'That’s the most you can redeem on one check.',
    'loyalty.redeem': 'Redeem',
    'loyalty.redeemFailed': 'That couldn’t be redeemed. Nothing has been used.',
    'loyalty.saved': 'You saved {amount}',
    'loyalty.newTotal': 'Your new total is {amount}',

    /*
     * The in-app feedback prompt, shown when the restaurant's feedback vendor is TouchTab.
     *
     * The instruction and the questions themselves are NOT here. They are authored per campaign in the
     * portal, arrive over the network, and are the restaurant's words in whatever language they wrote them —
     * translating them would mean translating them at write time, which is a portal feature and not a client
     * one. Everything the app itself says is here.
     */
    'feedback.title': 'How did we do?',
    'feedback.send': 'Send',
    'feedback.sending': 'Sending…',
    'feedback.skip': 'No thanks',
    'feedback.thanks': 'Thank you — we’ve passed this on to the restaurant.',
    'feedback.error': 'That didn’t send. Please try again.',
    /*
     * The comment box.
     *
     * The placeholder does not repeat the question — the question is already the field's label directly above
     * it, and a placeholder that restates it is read twice by a screen reader and vanishes the moment anyone
     * starts typing. It says the one thing the label does not: this is optional.
     *
     * The counter only appears near the cap. Its job is to explain why the keyboard is about to stop
     * responding, not to count for the whole 2000 characters.
     */
    'feedback.comment.placeholder': 'Optional',
    'feedback.comment.remaining': '{count} characters left',
    /*
     * The accessible name of each face. The number leads because it is the part that means something and the
     * part a guest comparing two questions needs; the word after it is what the face is trying to show.
     */
    'feedback.rating.1': '1 of 5, very dissatisfied',
    'feedback.rating.2': '2 of 5, dissatisfied',
    'feedback.rating.3': '3 of 5, neither',
    'feedback.rating.4': '4 of 5, satisfied',
    'feedback.rating.5': '5 of 5, very satisfied',

    /*
     * Payment failures.
     *
     * "Nothing was charged" appears wherever a guest could reasonably fear they have been. That sentence is
     * the whole message for most of them — the rest is detail they will read only if they are still worried.
     *
     * `insufficientFunds` is worded no differently from a plain decline on purpose. The server tells us why,
     * but this is a phone held at a table with other people at it, and printing the reason in large type is
     * not information the guest needs to act on.
     */
    'failure.declined.title': 'Your card was declined',
    'failure.declined.detail': 'Your bank turned the payment down. Trying a different card usually works.',
    'failure.expiredCard.title': 'That card has expired',
    'failure.expiredCard.detail': 'Check the expiry date, or try a different card.',
    'failure.insufficientFunds.title': 'Your card was declined',
    'failure.insufficientFunds.detail':
        'Your bank didn’t approve the amount. Trying a different card usually works.',
    'failure.authenticationFailed.title': 'Your bank couldn’t verify that',
    'failure.authenticationFailed.detail':
        'The security check didn’t finish, so nothing was charged. Try again, or use a different card.',
    'failure.checkAlreadyPaid.title': 'This check is already paid',
    'failure.checkAlreadyPaid.detail':
        'Someone at your table may have got there first. There’s nothing left to pay — ask your server if that’s a surprise.',
    'failure.checkChanged.title': 'Your check changed',
    'failure.checkChanged.detail':
        'Something was added or removed while you were paying, so nothing was charged.',
    'failure.checkChanged.newTotal': 'New total',
    'failure.gatewayUnavailable.title': 'We couldn’t reach the payment processor',
    'failure.gatewayUnavailable.detail':
        'Nothing was charged. Try again in a moment, or your server can take payment at the table.',
    'failure.expired.title': 'This took too long',
    'failure.expired.detail':
        'The payment timed out and nothing was charged. Scan the QR code on your check to start again.',
    /** For a code this build has never seen. The server's list grows without a client release. */
    'failure.unknown.title': 'Something went wrong',
    'failure.unknown.detail':
        'Nothing was charged. Try again, or your server can take payment at the table.',
    'failure.retry': 'Try again',
    'failure.backToCheck': 'Back to the check',

    /*
     * The document title. Read aloud first when a screen reader opens the page, and it is what a guest sees
     * in their tab list and share sheet, so it is translated like anything else on screen. The location
     * variant is used once the restaurant is known — a guest who has scanned checks at two places should be
     * able to tell the tabs apart.
     */
    'document.title': 'Pay your check',
    'document.titleAtLocation': '{name} — Pay your check',

    // Loading
    'status.loading': 'Loading your check',

    // Errors
    'error.invalidCode.title': 'We couldn’t open this check',
    'error.invalidCode.detail':
        'The code may have expired, or the check may already be closed. Try scanning the QR code on your check again, or ask your server for a hand.',
    'error.expired.title': 'Your session expired',
    'error.expired.detail': 'Scan the QR code on your check again to pick up where you left off.',
    'error.rateLimited.title': 'Too many attempts',
    'error.rateLimited.detail': 'Give it a minute, then scan the QR code on your check again.',
    'error.unreachable.title': 'We couldn’t reach the restaurant',
    'error.unreachable.detail':
        'Check your connection and try again. If it keeps happening, your server can take payment.',
    'error.checkLoad.title': 'We couldn’t load your check',
    'error.retry': 'Try again',
} as const;

export type MessageKey = keyof typeof en;

/** Every catalogue must cover every key the English one defines, or this fails to compile. */
export type Messages = Record<MessageKey, string>;

/*
 * Spanish. Kept slightly formal ("su cuenta" over "tu cuenta") because a guest is being addressed by a
 * business about money, which is the register a printed check uses.
 */
const es: Messages = {
    'check.table': 'Mesa {table}',
    'check.number': 'Cuenta {number}',
    'check.subtotal': 'Subtotal',
    'check.tax': 'Impuestos',
    'check.taxesAndFees': 'Impuestos y cargos',
    'check.feeInfoLabel': 'Acerca de impuestos y cargos',
    'check.feeExplanation':
        'Impuestos y cargos incluye todos los impuestos locales y estatales aplicables y un Cargo por Servicio Digital de {amount}.',
    'check.total': 'Total',
    'check.discount': 'Descuento',
    'check.surcharge': 'Cargo por servicio',
    'check.alreadyPaid': 'Ya pagado',
    'check.amountDue': 'Saldo a pagar',
    'check.pay': 'Pagar esta cuenta',
    'check.closedAction': 'Cuenta cerrada',
    'check.closedNotice': 'Esta cuenta ya fue cerrada. Consulte con su mesero si cree que hay un error.',
    'check.empty': 'Todavía no se ha agregado nada a esta cuenta.',
    'check.quantityLabel': 'Cantidad {count}',
    'check.itemsLabel': 'Artículos de su cuenta',

    'tip.title': 'Agregar propina',
    'tip.optionsLabel': 'Monto de la propina',
    'tip.custom': 'Otro monto',
    'tip.customLabel': 'Monto personalizado de la propina',
    'tip.customPlaceholder': 'Ingrese un monto',
    'tip.customInvalid': 'Ingrese un monto, por ejemplo {example}.',
    'tip.serviceCharge': 'Esta cuenta ya incluye un cargo por servicio de {amount}.',
    'tip.checkLine': 'Cuenta',
    'tip.tipLine': 'Propina',
    'tip.pay': 'Pagar {amount}',
    'tip.back': 'Volver a la cuenta',

    'payment.title': '¿Cómo desea pagar?',
    'payment.optionsLabel': 'Método de pago',
    /* Apple Pay and Google Pay are product names and stay untranslated, per both brands' guidelines. */
    'payment.card': 'Tarjeta',
    'payment.applePay': 'Apple Pay',
    'payment.googlePay': 'Google Pay',
    'payment.continue': 'Continuar',
    'payment.back': 'Volver a la propina',
    'payment.backToCheck': 'Volver a la cuenta',

    'payment.cardFrameTitle': 'Datos de la tarjeta',
    'payment.resolving': 'Finalizando su pago',
    'payment.payCard': 'Pagar',
    'payment.paying': 'Pagando…',

    'receipt.paidTitle': 'Pago completado',
    'receipt.paidAnnouncement': 'Pago completado. Se pagaron {amount}.',
    'receipt.checkNumber': 'Cuenta n.º',
    /*
     * "Atendido por" rather than "Servidor", which in Spanish is the machine. "Mesero/a" and "Camarero/a" are
     * both regional and both force a gender onto a name the POS hands us without one; this phrasing is what
     * Spanish-language receipts actually use and sidesteps both problems.
     */
    'receipt.server': 'Atendido por',
    'receipt.date': 'Fecha',
    'receipt.paymentLabel': 'Detalles del pago',
    'receipt.approvalCode': 'Código de autorización',

    'receipt.deliveryTitle': '¿Quiere una copia?',
    'receipt.emailIt': 'Por correo',
    'receipt.textIt': 'Por mensaje',
    'receipt.emailLabel': 'Correo electrónico',
    'receipt.smsLabel': 'Número de celular',
    'receipt.emailPlaceholder': 'usted@ejemplo.com',
    'receipt.smsPlaceholder': '(555) 123-4567',
    'receipt.send': 'Enviar',
    'receipt.sending': 'Enviando…',
    'receipt.emailSent': 'Enviado a {destination}.',
    'receipt.smsSent': 'Enviado por mensaje a {destination}.',
    'receipt.deliveryFailed': 'No pudimos enviarlo. Revise los datos e inténtelo de nuevo.',
    'receipt.deliveryTooMany': 'Demasiados intentos. Espere un minuto y vuelva a enviarlo.',
    'receipt.receiptLabel': 'Su recibo',
    'receipt.thankYou': 'Gracias por acompañarnos',
    'receipt.feedback': 'Cuéntenos cómo lo hicimos',
    'receipt.feedbackExternal': 'Se abre en una pestaña nueva',
    'receipt.cardEnding': 'Tarjeta terminada en {lastFour}',
    'receipt.done': 'Listo',
    'receipt.doneFinishing': 'Finalizando…',

    'loyalty.signIn': 'Iniciar sesión',
    'loyalty.join': 'Únase al programa',
    'loyalty.skip': 'Ahora no',
    'loyalty.continue': 'Continuar',
    'loyalty.working': 'Un momento…',
    'loyalty.identifier.phone': 'Número de móvil',
    'loyalty.identifier.email': 'Correo electrónico',
    'loyalty.identifier.either': 'Número de móvil o correo',
    'loyalty.code': 'Código de verificación',
    'loyalty.codeSent': 'Enviamos un código a {destination}.',
    'loyalty.verify': 'Verificar',
    'loyalty.codeWrong': 'Ese código no funcionó. Revíselo e inténtelo de nuevo.',
    'loyalty.error': 'Algo salió mal. Inténtelo de nuevo.',
    'loyalty.notFound': 'No encontramos esa cuenta. Puede pagar con normalidad.',
    'loyalty.greeting': 'Hola {name}',
    'loyalty.points': '{points} puntos',
    'loyalty.offers': 'Sus ofertas',
    'loyalty.rewards': 'Canjear puntos',
    'loyalty.pointCost': '{points} puntos',
    'loyalty.nothingAvailable': 'No hay nada que canjear en esta cuenta ahora mismo.',
    'loyalty.limitReached': 'Es el máximo que puede canjear en una cuenta.',
    'loyalty.redeem': 'Canjear',
    'loyalty.redeemFailed': 'No se pudo canjear. No se ha usado nada.',
    'loyalty.saved': 'Ahorró {amount}',
    'loyalty.newTotal': 'Su nuevo total es {amount}',

    'feedback.title': '¿Cómo lo hicimos?',
    'feedback.send': 'Enviar',
    'feedback.sending': 'Enviando…',
    'feedback.skip': 'No, gracias',
    'feedback.thanks': 'Gracias: se lo hemos hecho llegar al restaurante.',
    'feedback.error': 'No se pudo enviar. Inténtelo de nuevo.',
    'feedback.comment.placeholder': 'Opcional',
    'feedback.comment.remaining': 'Quedan {count} caracteres',
    'feedback.rating.1': '1 de 5, muy insatisfecho',
    'feedback.rating.2': '2 de 5, insatisfecho',
    'feedback.rating.3': '3 de 5, ni una cosa ni otra',
    'feedback.rating.4': '4 de 5, satisfecho',
    'feedback.rating.5': '5 de 5, muy satisfecho',

    'failure.declined.title': 'Su tarjeta fue rechazada',
    'failure.declined.detail': 'Su banco rechazó el pago. Suele funcionar con otra tarjeta.',
    'failure.expiredCard.title': 'Esa tarjeta está vencida',
    'failure.expiredCard.detail': 'Revise la fecha de vencimiento o use otra tarjeta.',
    'failure.insufficientFunds.title': 'Su tarjeta fue rechazada',
    'failure.insufficientFunds.detail': 'Su banco no aprobó el monto. Suele funcionar con otra tarjeta.',
    'failure.authenticationFailed.title': 'Su banco no pudo verificar el pago',
    'failure.authenticationFailed.detail':
        'La verificación de seguridad no se completó, así que no se cobró nada. Inténtelo de nuevo o use otra tarjeta.',
    'failure.checkAlreadyPaid.title': 'Esta cuenta ya está pagada',
    'failure.checkAlreadyPaid.detail':
        'Es posible que alguien de su mesa se haya adelantado. No queda nada por pagar: consulte con su mesero si le sorprende.',
    'failure.checkChanged.title': 'Su cuenta cambió',
    'failure.checkChanged.detail':
        'Se agregó o se quitó algo mientras pagaba, así que no se cobró nada.',
    'failure.checkChanged.newTotal': 'Nuevo total',
    'failure.gatewayUnavailable.title': 'No pudimos conectar con el procesador de pagos',
    'failure.gatewayUnavailable.detail':
        'No se cobró nada. Inténtelo de nuevo en un momento o su mesero puede cobrarle en la mesa.',
    'failure.expired.title': 'Esto tardó demasiado',
    'failure.expired.detail':
        'El pago venció y no se cobró nada. Escanee el código QR de su cuenta para empezar de nuevo.',
    'failure.unknown.title': 'Algo salió mal',
    'failure.unknown.detail':
        'No se cobró nada. Inténtelo de nuevo o su mesero puede cobrarle en la mesa.',
    'failure.retry': 'Intentar de nuevo',
    'failure.backToCheck': 'Volver a la cuenta',

    'document.title': 'Pague su cuenta',
    'document.titleAtLocation': '{name} — Pague su cuenta',

    'status.loading': 'Cargando su cuenta',

    'error.invalidCode.title': 'No pudimos abrir esta cuenta',
    'error.invalidCode.detail':
        'Es posible que el código haya vencido o que la cuenta ya esté cerrada. Vuelva a escanear el código QR de su cuenta o pida ayuda a su mesero.',
    'error.expired.title': 'Su sesión venció',
    'error.expired.detail': 'Vuelva a escanear el código QR de su cuenta para continuar donde se quedó.',
    'error.rateLimited.title': 'Demasiados intentos',
    'error.rateLimited.detail': 'Espere un momento y vuelva a escanear el código QR de su cuenta.',
    'error.unreachable.title': 'No pudimos conectar con el restaurante',
    'error.unreachable.detail':
        'Revise su conexión e inténtelo de nuevo. Si el problema continúa, su mesero puede cobrarle.',
    'error.checkLoad.title': 'No pudimos cargar su cuenta',
    'error.retry': 'Intentar de nuevo',
};

export const catalogues = { en, es } satisfies Record<string, Messages>;

export type LanguageCode = keyof typeof catalogues;

export const SUPPORTED_LANGUAGES = Object.keys(catalogues) as LanguageCode[];

export const DEFAULT_LANGUAGE: LanguageCode = 'en';
